/**
 * The browser shows a stored file through `/v/{id}`, the gate's door. A stored
 * storage URL that carries an id (old messages, attachments, presented outputs)
 * maps to that same-origin door, where `apps/web/src/app/v/[fileId]/route.ts`
 * asks the gate who may read it and hands out a short pass. A stored file WITH
 * an id is shown only through `/v`; a pre-convention object name that carries
 * no UUID keeps its address.
 *
 * The door also fixes HTML: Supabase Storage serves HTML as `text/plain`
 * (anti-abuse), while `/v/{id}` proxies it with the correct `text/html` header.
 */

import {
  fileIdFromStorageUrl,
  storageObjectFromUrl,
  viewerPath,
} from "@bash/shared";
import { storageProxyPath } from "@/lib/storage-proxy";

const UUID = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

/**
 * The file (or creation) id inside a storage URL for the two user buckets: public,
 * signed, or a render transform; at `<userId>/<id>.<ext>` or a presented output's
 * `<userId>/outputs/<task>/<id>.<ext>`. Null for anything else (a /v link, an
 * external URL, a blob, an app face). The shared parsers are the one home of this
 * convention.
 */
export function extractFileIdFromCdnUrl(
  url: string | undefined | null
): string | null {
  return storageObjectFromUrl(url) ? fileIdFromStorageUrl(url) : null;
}

/**
 * Returns the same-origin proxy URL (`/v/{fileId}`) for any user-file URL.
 *
 * Accepts both shapes that flow through the app:
 *   - Direct Supabase CDN URL (TaskOutput.url) → extract fileId, return `/v/{fileId}`
 *   - Already-proxied URL (UserFile.downloadUrl) → normalise to `/v/{fileId}`
 *
 * Returns null when the URL is neither shape (e.g. blob URLs), and for anything
 * that is not a string (a tool result is untyped JSON), so every caller gets the
 * type guard structurally.
 */
export function fileViewerUrl(url: string | undefined | null): string | null {
  if (typeof url !== "string" || !url) return null;
  // Same-origin proxy path — extract the id and return canonically.
  const proxyMatch = url.match(
    new RegExp(`/v/(${UUID.source})(?:[/?#]|$)`, "i")
  );
  if (proxyMatch) return `/v/${proxyMatch[1]}`;
  // Supabase CDN URL.
  const fileId = extractFileIdFromCdnUrl(url);
  return fileId ? `/v/${fileId}` : null;
}

/**
 * The url a job result renders by: its door (fileViewerUrl) unless the server flagged
 * the result `noDoor`, a live pass for an output no creation names, whose door
 * (/v/<job id>) would name nothing: that pass (a picture or a clip) is read
 * sandboxed on our own origin (storageProxyPath). A result from before the flag carries none and keeps
 * mapping. Undefined when the result has no url.
 */
export function resultViewerUrl(result: {
  url?: unknown;
  noDoor?: unknown;
}): string | undefined {
  if (typeof result.url !== "string") return undefined;
  if (result.noDoor === true) return storageProxyPath(result.url) ?? result.url;
  return fileViewerUrl(result.url) ?? result.url;
}

/**
 * The url a stored file rides on as an attachment: its /v door, absolute on
 * this page's origin. The server reads only the id in its path and asks the
 * gate (attachmentFileId: theirs, or shared with them). Absolute because the
 * task socket and the ask card validate a file with FileAttachmentSchema
 * (`url: z.string().url()`), which refuses a bare "/v/<id>". Browser only: a
 * lane mints it when a file lands in the strip.
 */
export function attachmentDoor(fileId: string): string {
  return `${window.location.origin}${viewerPath(fileId)}`;
}
